import fp from 'lodash/fp';
import { RSAA } from 'redux-api-middleware';
import { race, take } from 'redux-saga/effects';
import { appendUrlQueryParameters, noCapMap } from '../../../utils';
import { jsonErrorPayloadConverter, jsonPayloadConverter } from './utils';

export const API_ROOT = '/api/v1';

export const methods = {
  get: 'GET',
  post: 'POST',
  put: 'PUT',
  delete: 'DELETE',
  patch: 'PATCH'
}

export const statusCodes = {
  unauthorised: 401
}

// Assume json content
export const standardHeaders = {
  'Content-Type': 'application/json'
}

export const authenticatedHeaders = ({ idToken, mockUserId }, ignoreContentType) => ({
  ...(
    ignoreContentType ?
      {} :
      standardHeaders
  ),
  'X-Auth-Token': idToken,
  ...(
    mockUserId ? { 'mockUserId': mockUserId } : {}
  )
});

// Wrap the final api action definition in a RSAA
const getRSAA = (actionDefinition) => ({
  [RSAA]: actionDefinition
})

// Base api action definition
const apiAction = (method, headers, endpoint, actionTypes) => ({
  endpoint: `${API_ROOT}/${endpoint}`,
  method: method,
  headers: headers,
  credentials: 'same-origin',
  // Assume default action types
  types: [
    actionTypes.request(),
    // Assume json payloads for success/failed
    actionTypes.success(jsonPayloadConverter),
    actionTypes.failed(jsonErrorPayloadConverter)
  ],
  fetch: (...args) =>
    fetch(...[
      fp.flow(
        fp.head,
        appendUrlQueryParameters({ noCache: Date.now() })
      )(args), ...fp.tail(args)])
})

// Used to extend action with body for post/put actions
const bodyAction = (body) => ({
  body: JSON.stringify(body)
});

const formDataAction = (data, fileData) => {
  const formData = new FormData();

  if (fileData) {
    if (fileData.constructor === Array) {
      noCapMap((f, index) => {
        if (f.hasOwnProperty('key')) {
          formData.append(f.key, f.value)
        } else {
          formData.append(`file${index}`, f)
        }
      })(fp.filter(f => (f !== null) && (!f.hasOwnProperty('value') || f.value !== null))(fileData))
    } else {
      if (fileData.hasOwnProperty('key')) {
        formData.append(fileData.key, fileData.value);
      } else {
        formData.append('file', fileData);
      }
    }
  }

  formData.append('data', JSON.stringify(data));

  return { body: formData };
}

const bodyApiAction = (method, headers, body, endpoint, types) => ({
  ...apiAction(method, headers, endpoint, types),
  ...bodyAction(body)
});

const formDataApiAction = (method, headers, formData, endpoint, types) => ({
  ...apiAction(method, headers, endpoint, types),
  ...formDataAction(formData.data, formData.file)
});

export const apiActions = {
  get: (endpoint, actionTypes) => getRSAA(apiAction(methods.get, standardHeaders, endpoint, actionTypes)),
  post: (endpoint, body, actionTypes) => getRSAA(bodyApiAction(methods.post, standardHeaders, body, endpoint, actionTypes)),
  put: (endpoint, body, actionTypes) => getRSAA(bodyApiAction(methods.put, standardHeaders, body, endpoint, actionTypes)),
  patch: (endpoint, body, actionTypes) => getRSAA(bodyApiAction(methods.patch, standardHeaders, body, endpoint, actionTypes)),
  delete: (endpoint, actionTypes) => getRSAA(apiAction(methods.delete, standardHeaders, endpoint, actionTypes)),
  authenticated: {
    get: (endpoint, authToken, actionTypes) => authToken && getRSAA(apiAction(methods.get, authenticatedHeaders(authToken), endpoint, actionTypes)),
    post: (endpoint, authToken, body, actionTypes) => authToken && getRSAA(bodyApiAction(methods.post, authenticatedHeaders(authToken), body, endpoint, actionTypes)),
    put: (endpoint, authToken, body, actionTypes) => authToken && getRSAA(bodyApiAction(methods.put, authenticatedHeaders(authToken), body, endpoint, actionTypes)),
    patch: (endpoint, authToken, body, actionTypes) => authToken && getRSAA(bodyApiAction(methods.patch, authenticatedHeaders(authToken), body, endpoint, actionTypes)),
    postFormData: (endpoint, authToken, formData, actionTypes) => authToken && getRSAA(formDataApiAction(methods.post, authenticatedHeaders(authToken, true), formData, endpoint, actionTypes)),
    putFormData: (endpoint, authToken, formData, actionTypes) => authToken && getRSAA(formDataApiAction(methods.put, authenticatedHeaders(authToken, true), formData, endpoint, actionTypes)),
    delete: (endpoint, authToken, actionTypes) => authToken && getRSAA(apiAction(methods.delete, authenticatedHeaders(authToken), endpoint, actionTypes))
  }
}

/**
 * Matcher for the failed API actions, any actions matches this pattern will be considered as failed API actions.
 * @see https://redux-saga.js.org/docs/api/
 * @param {object} action that is received to be tested for match. 
 * @param {object} apiAction to be tested.
 * @return {boolean} true if the action matches the API action, otherwise false.
 */
export const failedApiActionsMatcher = (action, apiAction) => action.type === apiAction.failed().type || (action.type === apiAction.request().type && action.error);

/**
 * Returns a Redux saga that takes either the success or failed action of the provided API action.
 */
export const apiActionResult = (apiAction) => {
  return race({
    success: take(apiAction.success),
    failed: take((action) => failedApiActionsMatcher(action, apiAction))
  });
}


// WEBPACK FOOTER //
// ./src/state/modules/common/apiActions.js